<section class="module-editor" [class.unavailable]="operations().length === 0">
  <header class="module-heading">
    <div class="module-mark" aria-hidden="true">
      <mat-icon>extension</mat-icon>
    </div>
    <div>
      <span class="eyebrow">Add a module</span>
      <h3>{{ node().label }}</h3>
      @if (node().description) {
        <p>{{ node().description }}</p>
      }
    </div>
  </header>

  @if (operations().length === 0) {
    <div class="empty-state" role="status">
      <mat-icon>info</mat-icon>
      <div>
        <strong>This module needs advanced setup</strong>
        <span>There is no safe friendly form for it yet. Nothing will be added until one is available.</span>
      </div>
    </div>
  } @else {
    <mat-form-field appearance="outline" class="operation-picker">
      <mat-label>What should this module do?</mat-label>
      <mat-select
        [value]="selectedAction()"
        (selectionChange)="selectOperation($event.value)"
        aria-label="Module operation">
        @for (operation of operations(); track operation.action) {
          <mat-option [value]="operation.action">{{ operation.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    @if (selectedOperation(); as operation) {
      <div class="operation-summary">
        <span class="effect" [class]="'effect ' + operation.effect">
          <mat-icon>{{ effectIcon(operation.effect) }}</mat-icon>
          {{ effectLabel(operation.effect) }}
        </span>
        <div>
          <strong>{{ operation.label }}</strong>
          @if (operation.help) {
            <p>{{ operation.help }}</p>
          }
        </div>
      </div>

      <div class="field-list">
        @for (entry of visibleFields(); track entry.path) {
          <section class="field-card">
            <div class="field-heading">
              <div>
                <label [id]="fieldLabelId(entry.path, [])">{{ entry.field.label }}</label>
                @if (isFieldRequired(entry.path)) {
                  <span class="required">Required</span>
                }
              </div>
              @if (entry.field.help) {
                <p [id]="fieldHelpId(entry.path, [])">{{ entry.field.help }}</p>
              }
            </div>
            <ng-container
              *ngTemplateOutlet="shapeEditor; context: {
                fieldPath: entry.path,
                shape: entry.field.shape,
                address: [],
                controlLabel: entry.field.label,
                labelledBy: fieldLabelId(entry.path, []),
                describedBy: entry.field.help ? fieldHelpId(entry.path, []) : null
              }" />
          </section>
        }
      </div>

      @if (requirementHint()) {
        <div class="requirement-note" [class.complete]="draft() !== null">
          <mat-icon>{{ draft() ? 'check_circle' : 'tips_and_updates' }}</mat-icon>
          <span>
            @if (draft()) {
              The required information is ready.
            } @else {
              Complete: {{ requirementHint() }}.
            }
          </span>
        </div>
      }
    }
  }
</section>

<ng-template
  #shapeEditor
  let-fieldPath="fieldPath"
  let-shape="shape"
  let-address="address"
  let-controlLabel="controlLabel"
  let-labelledBy="labelledBy"
  let-describedBy="describedBy">
  @if (shapeKind(shape) === 'oneOf') {
    <div class="shape-stack">
      <mat-form-field appearance="outline" class="kind-picker">
        <mat-label>How do you want to enter this?</mat-label>
        <mat-select
          [aria-label]="controlLabel"
          [aria-describedby]="describedBy"
          [value]="selectedVariant(fieldPath, address, shape)"
          (selectionChange)="selectVariant(fieldPath, address, shape, $event.value)">
          @for (variant of shape.oneOf; track $index) {
            <mat-option [value]="$index">{{ optionLabel(shape, $index) }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <ng-container
        *ngTemplateOutlet="shapeEditor; context: {
          fieldPath,
          shape: variantShape(shape, selectedVariant(fieldPath, address, shape)),
          address,
          controlLabel,
          labelledBy,
          describedBy
        }" />
    </div>
  } @else if (hasTypeChoice(shape)) {
    <div class="shape-stack">
      <mat-form-field appearance="outline" class="kind-picker">
        <mat-label>Value type</mat-label>
        <mat-select
          [aria-label]="controlLabel"
          [aria-describedby]="describedBy"
          [value]="selectedType(fieldPath, address, shape)"
          (selectionChange)="selectType(fieldPath, address, shape, $event.value)">
          @for (type of typeOptions(shape); track type) {
            <mat-option [value]="type">{{ humanize(type) }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <ng-container
        *ngTemplateOutlet="shapeEditor; context: {
          fieldPath,
          shape: shapeForType(shape, selectedType(fieldPath, address, shape)),
          address,
          controlLabel,
          labelledBy,
          describedBy
        }" />
    </div>
  } @else if (isFixedSingleValueEnum(shape)) {
    <div class="fixed-value" [attr.aria-label]="controlLabel" [attr.aria-describedby]="describedBy">
      <span>Fixed value</span>
      <strong>{{ enumLabel(shape.enum![0]) }}</strong>
    </div>
  } @else if (isBindingCapableShape(shape)) {
    <div class="binding-editor">
      <mat-form-field appearance="outline" class="binding-picker">
        <mat-label>Use</mat-label>
        <mat-select
          [attr.aria-label]="controlLabel + ' value source'"
          [attr.aria-describedby]="describedBy"
          [value]="bindingChoice(fieldPath, address)"
          (selectionChange)="selectBinding(fieldPath, address, shape, $event.value)">
          <mat-option value="fixed">Fixed value</mat-option>
          @for (binding of bindingOptions(shape); track binding.value) {
            <mat-option [value]="binding.value">{{ binding.label }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      @if (!bindingChoiceAvailable(fieldPath, address, shape)) {
        <span class="binding-error" role="alert">{{ bindingUnavailableMessage(fieldPath, address) }}</span>
      } @else if (bindingChoice(fieldPath, address) === 'fixed') {
        @if (shapeKind(shape) === 'enum') {
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Choose a value</mat-label>
            <mat-select
              [aria-label]="controlLabel"
              [aria-describedby]="describedBy"
              [value]="valueAt(fieldPath, address)"
              (selectionChange)="setScalarValue(fieldPath, address, $event.value)">
              @for (option of shape.enum; track $index) {
                <mat-option [value]="option">{{ enumLabel(option) }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        } @else {
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Text</mat-label>
            <input
              matInput
              [id]="fieldControlId(fieldPath, address, 'text')"
              [attr.aria-label]="controlLabel"
              [attr.aria-describedby]="describedBy"
              type="text"
              [attr.minlength]="shape.minLength ?? null"
              [attr.maxlength]="shape.maxLength ?? null"
              [value]="stringValue(fieldPath, address)"
              (input)="setTextValue(fieldPath, address, $event)">
          </mat-form-field>
        }
      }
    </div>
  } @else if (shapeKind(shape) === 'enum') {
    <mat-form-field appearance="outline" class="full-width">
      <mat-label>Choose a value</mat-label>
      <mat-select
        [aria-label]="controlLabel"
        [aria-describedby]="describedBy"
        [value]="valueAt(fieldPath, address)"
        (selectionChange)="setScalarValue(fieldPath, address, $event.value)">
        @for (option of shape.enum; track $index) {
          <mat-option [value]="option">{{ enumLabel(option) }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
  } @else if (shapeKind(shape) === 'boolean') {
    <mat-checkbox
      [aria-label]="controlLabel"
      [aria-describedby]="describedBy ?? ''"
      [checked]="booleanValue(fieldPath, address)"
      (change)="setBooleanValue(fieldPath, address, $event.checked)">
      Yes
    </mat-checkbox>
  } @else if (shapeKind(shape) === 'number' || shapeKind(shape) === 'integer') {
    <mat-form-field appearance="outline" class="full-width">
      <mat-label>Number</mat-label>
      <input
        matInput
        [id]="fieldControlId(fieldPath, address, 'number')"
        [attr.aria-label]="controlLabel"
        [attr.aria-describedby]="describedBy"
        type="number"
        [step]="shapeKind(shape) === 'integer' ? 1 : 'any'"
        [min]="shape.minimum ?? null"
        [max]="shape.maximum ?? null"
        [value]="numberValue(fieldPath, address)"
        (input)="setNumberValue(fieldPath, address, $event, shapeKind(shape) === 'integer')">
    </mat-form-field>
  } @else if (shapeKind(shape) === 'null') {
    <button
      mat-stroked-button
      type="button"
      [attr.aria-label]="'Use an empty value for ' + controlLabel"
      (click)="setScalarValue(fieldPath, address, null)">
      <mat-icon>remove_circle_outline</mat-icon>
      Use an empty value
    </button>
  } @else if (shapeKind(shape) === 'array') {
    <div class="collection-editor">
      @for (item of arrayValues(fieldPath, address); track $index) {
        <div class="collection-row">
          <span class="item-number">{{ $index + 1 }}</span>
          <div class="collection-content">
            <ng-container
              *ngTemplateOutlet="shapeEditor; context: {
                fieldPath,
                shape: shape.items,
                address: childAddress(address, $index),
                controlLabel: controlLabel + ' item ' + ($index + 1),
                labelledBy,
                describedBy
              }" />
          </div>
          <button
            mat-icon-button
            type="button"
            [attr.aria-label]="'Remove ' + controlLabel + ' item ' + ($index + 1)"
            (click)="removeArrayItem(fieldPath, address, $index)">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      }
      <button
        mat-stroked-button
        type="button"
        [attr.aria-label]="'Add ' + controlLabel + ' item'"
        [disabled]="shape.maxItems !== undefined && arrayValues(fieldPath, address).length >= shape.maxItems"
        (click)="addArrayItem(fieldPath, address, shape)">
        <mat-icon>add</mat-icon>
        Add item
      </button>
    </div>
  } @else if (shapeKind(shape) === 'object') {
    <div class="object-editor">
      @for (property of objectProperties(shape); track property.key) {
        <section class="nested-field">
          <label [id]="fieldLabelId(fieldPath, childAddress(address, property.key))">
            {{ humanize(property.key) }}
            @if (isObjectPropertyRequired(shape, property.key)) {
              <span class="required">Required</span>
            }
          </label>
          <ng-container
            *ngTemplateOutlet="shapeEditor; context: {
              fieldPath,
              shape: property.shape,
              address: childAddress(address, property.key),
              controlLabel: humanize(property.key),
              labelledBy: fieldLabelId(fieldPath, childAddress(address, property.key)),
              describedBy: null
            }" />
        </section>
      }
      @if (shape.additionalProperties && shape.additionalProperties !== false) {
        <div class="map-editor">
          @for (entry of mapEntries(fieldPath, address); track entry.key) {
            <div class="map-row">
              <mat-form-field appearance="outline" class="map-key">
                <mat-label>Field name</mat-label>
                <input
                  matInput
                  [attr.aria-label]="controlLabel + ' named value ' + ($index + 1) + ' field name'"
                  [value]="entry.key"
                  (change)="renameMapEntry(fieldPath, address, entry.key, $event)">
              </mat-form-field>
              <ng-container
                *ngTemplateOutlet="shapeEditor; context: {
                  fieldPath,
                  shape: shape.additionalProperties,
                  address: childAddress(address, entry.key),
                  controlLabel: controlLabel + ' ' + humanize(entry.key) + ' value',
                  labelledBy: null,
                  describedBy
                }" />
              <button
                mat-icon-button
                type="button"
                [attr.aria-label]="'Remove ' + controlLabel + ' named value ' + entry.key"
                (click)="removeMapEntry(fieldPath, address, entry.key)">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          }
          <button
            mat-stroked-button
            type="button"
            [attr.aria-label]="'Add ' + controlLabel + ' named value'"
            (click)="addMapEntry(fieldPath, address, shape)">
            <mat-icon>add</mat-icon>
            Add named value
          </button>
        </div>
      }
    </div>
  } @else {
    <mat-form-field appearance="outline" class="full-width">
      <mat-label>Text</mat-label>
      <input
        matInput
        [id]="fieldControlId(fieldPath, address, 'text')"
        [attr.aria-label]="controlLabel"
        [attr.aria-describedby]="describedBy"
        type="text"
        [attr.minlength]="shape.minLength ?? null"
        [attr.maxlength]="shape.maxLength ?? null"
        [value]="stringValue(fieldPath, address)"
        (input)="setTextValue(fieldPath, address, $event)">
    </mat-form-field>
  }
</ng-template>
